import { useState, useContext, useEffect, useRef, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Add, Search, Close, Edit, ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon, KeyboardArrowDown, KeyboardArrowRight } from '@mui/icons-material';
import PageArea from '../../components/shared/PageArea';
import PageTitle from '../../components/shared/PageTitle';
import { Section } from '../../components/ui/Section';
import { Pill } from '../../components/shared/Pill';
import { TableHeader } from '../../components/forms/TableHeader';
import CustomPagination from '../../components/forms/Pagination';
import { UserContext } from '../../components/shared/useUser';
import useNswagClient from '../../hooks/api/useNswagClient';
import { RecipeV2, RecipeStatus } from './types';
import { applyRoundAndFormat, exportFile } from '../../utils';
import useCurrencyCodeConfiguration from '../../hooks/configurations/useCurrencyCodeConfiguration';
import useRecipeV2List from './hooks/useRecipeV2List';
import useRecipeV2Api from './hooks/useRecipeV2Api';
import BulkManageDialog from './components/BulkManageDialog';
import { RecipeComponentsTable } from './components/RecipeComponentsTable';
import AllergenChips from './components/RecipeV2AllergenChips';
import { ListPageTemplate } from '../../components/page-templates/list-page/ListPageTemplate';
import { EmptyTable } from '../../components/shared/EmptyTable';
import type { TableColumn, TableSectionProps } from '../../components/shared/tables/TableSection';
import { canManageRecipesV2Bulk } from './bulkManageAccess';

const SEARCH_DEBOUNCE_MS = 400;
const MIN_SEARCH_LENGTH = 3;

const statusColor: Record<RecipeStatus, 'success' | 'warning' | 'info' | 'default' | 'error'> = {
  [RecipeStatus.Draft]: 'default',
  [RecipeStatus.Scheduled]: 'info',
  [RecipeStatus.Active]: 'success',
  [RecipeStatus.Expired]: 'error',
  [RecipeStatus.Archived]: 'warning',
};

const statusOptions = Object.values(RecipeStatus);

const RecipeV2Page = () => {
  const { t } = useTranslation('recipeV2');
  const navigate = useNavigate();
  const { hasPermissionTo, selectedStore } = useContext(UserContext);
  const selectedFranchiseId = selectedStore?.franchiseId;
  const nswagApi = useNswagClient();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [statusFilter, setStatusFilter] = useState<RecipeStatus | ''>('');
  const [filterCategory, setFilterCategory] = useState('');
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
  const [categoryOptions, setCategoryOptions] = useState<string[]>([]);

  const { recipes, totalCount, isLoading, error, loadRecipes, replaceRecipe } = useRecipeV2List();
  const recipeApi = useRecipeV2Api();
  const { currencyCode, getCurrencyCodeConfiguration } = useCurrencyCodeConfiguration();
  const hasWritePermission = hasPermissionTo(['RecipeV2Write']);
  const hasBulkManagePermission = canManageRecipesV2Bulk(hasPermissionTo);
  const hasExportPermission = hasPermissionTo(['RecipeV2Read']);
  const [bulkManageOpen, setBulkManageOpen] = useState(false);
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
  const [loadingComponentIds, setLoadingComponentIds] = useState<Set<number>>(new Set());
  const [expandError, setExpandError] = useState<string | null>(null);

  useEffect(() => {
    getCurrencyCodeConfiguration();
  }, []);

  useEffect(() => {
    if (selectedStore?.storeNumber) {
      nswagApi.getPrimaryCategoriesForStore(selectedStore.storeNumber)
        .then((res) => {
          const names = (res?.data ?? [])
            .map((c: { primaryCategoryName?: string }) => c.primaryCategoryName ?? '')
            .filter(Boolean)
            .sort((a, b) => a.localeCompare(b));
          setCategoryOptions(names);
        })
        .catch(() => setCategoryOptions([]));
    }
  }, [selectedStore?.storeNumber]);

  useEffect(() => {
    loadRecipes(debouncedSearch, statusFilter, filterCategory, page, rowsPerPage);
  }, [debouncedSearch, statusFilter, filterCategory, page, rowsPerPage, loadRecipes]);

  // Debounce search input — only fires after 3+ characters or empty
  const handleSearchChange = (value: string) => {
    setSearch(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (value.length >= MIN_SEARCH_LENGTH || value.length === 0) {
        setDebouncedSearch(value);
        setPage(0);
      }
    }, SEARCH_DEBOUNCE_MS);
  };

  useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current); }, []);

  const handleRowClick = (recipe: RecipeV2) => {
    navigate(`/range/recipes/${recipe.id}`);
  };

  const handleClearFilters = () => {
    setStatusFilter('');
    setFilterCategory('');
    setSearch('');
    setDebouncedSearch('');
  };

  const handleExport = async () => {
    try {
      // Recipes are franchise-level - export does not require a selected store.
      const blob = await recipeApi.exportRecipes(debouncedSearch, statusFilter, filterCategory, 'xlsx');
      exportFile(blob, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'Recipes.xlsx');
    } catch { /* noop */ }
  };

  const toggleExpand = async (recipe: RecipeV2) => {
    if (loadingComponentIds.has(recipe.id)) return;
    setExpandError(null);
    if (expandedIds.has(recipe.id)) {
      setExpandedIds((prev) => {
        const next = new Set(prev);
        next.delete(recipe.id);
        return next;
      });
      return;
    }
    if (!recipe.components?.length) {
      setLoadingComponentIds(prev => new Set(prev).add(recipe.id));
      try {
        replaceRecipe(await recipeApi.getRecipeDetail(recipe.id));
      } catch (cause) {
        // Surface the failure - silently returning here left the chevron looking unresponsive with no feedback.
        setExpandError(cause instanceof Error ? cause.message : 'Failed to load recipe components.');
        return;
      } finally {
        setLoadingComponentIds((prev) => {
          const next = new Set(prev);
          next.delete(recipe.id);
          return next;
        });
      }
    }
    setExpandedIds((prev) => {
      const next = new Set(prev);
      next.add(recipe.id);
      return next;
    });
  };

  const [expandedComponentPaths, setExpandedComponentPaths] = useState<Set<string>>(new Set());
  const toggleComponentPath = (path: string) => {
    setExpandedComponentPaths((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  };

  const maxPages = Math.ceil(totalCount / rowsPerPage);

  const columns: TableColumn<RecipeV2>[] = [
    { id: 'expand', label: '', render: (recipe) => {
      const hasComponents = (recipe.componentCount ?? recipe.components?.length ?? 0) > 0;
      const isExpanded = expandedIds.has(recipe.id);
      return hasComponents ? <IconButton size="small"
        disabled={loadingComponentIds.has(recipe.id)}
        onClick={(event) => { event.stopPropagation(); void toggleExpand(recipe); }}>
        {loadingComponentIds.has(recipe.id) ? <CircularProgress size={16} /> : isExpanded ? <KeyboardArrowDown fontSize="small" /> : <KeyboardArrowRight fontSize="small" />}
      </IconButton> : null;
    } },
    { id: 'sku', label: t('sku'), render: (recipe) => <Typography variant="textSM"
      fontFamily="monospace">{recipe.itemNumber}</Typography> },
    { id: 'name', label: t('name'), render: (recipe) => <Typography variant="textSM"
      fontWeight={500}>{recipe.description || '-'}</Typography> },
    { id: 'status', label: t('statusLabel'), align: 'center', render: (recipe) => <Chip label={recipe.status ? t(`status.${recipe.status.charAt(0).toLowerCase() + recipe.status.slice(1)}`) : 'Unknown'}
      color={recipe.status ? statusColor[recipe.status] ?? 'default' : 'default'}
      size="small" /> },
    { id: 'version', label: t('version'), align: 'center', render: (recipe) => `v${recipe.currentVersion ?? 1}` },
    { id: 'category', label: t('category'), render: (recipe) => recipe.primaryCategory || '-' },
    { id: 'cost', label: t('cost'), align: 'right', render: (recipe) => recipe.costIsComplete && recipe.totalCost != null ? applyRoundAndFormat(recipe.totalCost, currencyCode) : '—' },
    { id: 'costPerPortion', label: t('costPerPortion'), align: 'right', render: (recipe) => recipe.costIsComplete && recipe.costPerPortion != null ? applyRoundAndFormat(recipe.costPerPortion, currencyCode, 4) : '—' },
    { id: 'allergens', label: t('allergensLabel'), render: (recipe) => recipe.allergenSummary?.length ? <AllergenChips allergens={recipe.allergenSummary}
      size="small"
      max={3}
      spacing={0.5} /> : '—' },
    { id: 'actions', label: t('actions.title'), align: 'center', render: (recipe) => hasWritePermission ? <IconButton size="small"
      onClick={(event) => { event.stopPropagation(); handleRowClick(recipe); }}><Edit fontSize="small" /></IconButton> : null },
  ];

  const table: TableSectionProps<RecipeV2> = {
    title: t('title'), columns, rows: recipes, getRowId: (recipe) => recipe.id,
    onRowClick: handleRowClick, recordCount: totalCount, recordCountLabel: t('title').toLowerCase(),
    searchValue: search, searchPlaceholder: t('searchPlaceholder'), onSearchChange: handleSearchChange,
    page, maxPages: Math.max(maxPages, 1), onPageChange: setPage,
    // Keep a visible spinner for both the initial load and subsequent refreshes.
    // TableLoading provides the skeleton; the overlay makes the loading state explicit.
    loading: isLoading && recipes.length === 0, loadingOverlay: isLoading,
    emptyContent: <EmptyTable text={t('title')}
      searchTerm={search}
      onClear={handleClearFilters}
      onAdd={() => navigate('/range/recipes/new')}
      addItemText={t('newRecipe')}
      onAddIsCreating={false}
      showOnAdd={hasWritePermission}
      withPaginationShown={false} />,
    renderBody: (tableRows) => tableRows.map((recipe) => {
      const hasComponents = (recipe.componentCount ?? recipe.components?.length ?? 0) > 0;
      const isExpanded = expandedIds.has(recipe.id);
      return <Fragment key={recipe.id}>
        <TableRow hover
          sx={{ cursor: 'pointer' }}
          onClick={() => handleRowClick(recipe)}>
          <TableCell sx={{ px: 0.5 }}>
            {hasComponents && <IconButton size="small"
              disabled={loadingComponentIds.has(recipe.id)}
              onClick={(event) => { event.stopPropagation(); void toggleExpand(recipe); }}>
              {loadingComponentIds.has(recipe.id) ? <CircularProgress size={16} /> : isExpanded ? <KeyboardArrowDown fontSize="small" /> : <KeyboardArrowRight fontSize="small" />}
            </IconButton>}
          </TableCell>
          <TableCell><Typography variant="textSM"
            fontFamily="monospace">{recipe.itemNumber}</Typography></TableCell>
          <TableCell><Typography variant="textSM"
            fontWeight={500}>{recipe.description || '-'}</Typography></TableCell>
          <TableCell align="center"><Chip label={recipe.status ? t(`status.${recipe.status.charAt(0).toLowerCase() + recipe.status.slice(1)}`) : 'Unknown'}
            color={recipe.status ? statusColor[recipe.status] ?? 'default' : 'default'}
            size="small" /></TableCell>
          <TableCell align="center">v{recipe.currentVersion ?? 1}</TableCell>
          <TableCell>{recipe.primaryCategory || '-'}</TableCell>
          <TableCell align="right">{recipe.costIsComplete && recipe.totalCost != null ? applyRoundAndFormat(recipe.totalCost, currencyCode) : '—'}</TableCell>
          <TableCell align="right">{recipe.costIsComplete && recipe.costPerPortion != null ? applyRoundAndFormat(recipe.costPerPortion, currencyCode, 4) : '—'}</TableCell>
          <TableCell>{recipe.allergenSummary?.length ? <AllergenChips allergens={recipe.allergenSummary}
            size="small"
            max={3}
            spacing={0.5} /> : '—'}</TableCell>
          <TableCell align="center">{hasWritePermission && <IconButton size="small"
            onClick={(event) => { event.stopPropagation(); handleRowClick(recipe); }}><Edit fontSize="small" /></IconButton>}</TableCell>
        </TableRow>
        {hasComponents && isExpanded && <TableRow>
          <TableCell colSpan={10}
            sx={{ py: 2, pl: { xs: 2, sm: 7 }, pr: { xs: 2, sm: 5 }, bgcolor: 'grey.50' }}>
            <RecipeComponentsTable recipeName={recipe.description}
              components={recipe.components ?? []}
              keyPrefix={String(recipe.id)}
              expandedPaths={expandedComponentPaths}
              toggleExpanded={toggleComponentPath}
              t={t}
              currencyCode={currencyCode} />
          </TableCell>
        </TableRow>}
      </Fragment>;
    }),
  };

  const quickFilters = (
    <Stack direction="row"
      spacing={1}
      flexWrap="wrap">
      {statusOptions.map((status) => <Chip key={status}
        label={t(`status.${status.charAt(0).toLowerCase() + status.slice(1)}`)}
        color={statusFilter === status ? 'primary' : 'default'}
        onClick={() => { setStatusFilter((previous) => previous === status ? '' : status); setPage(0); }} />)}
    </Stack>
  );

  const advancedFilters = (
    <Grid container
      spacing={3}>
      <Grid item
        lg={3}
        sm={6}
        xs={12}><TextField select
          label={t('statusLabel')}
          value={statusFilter}
          onChange={(event) => { setStatusFilter(event.target.value as RecipeStatus | ''); setPage(0); }}
          fullWidth
          size="small"><MenuItem value="">{t('filters.all')}</MenuItem>{statusOptions.map((status) => <MenuItem key={status}
            value={status}>{t(`status.${status.charAt(0).toLowerCase() + status.slice(1)}`)}</MenuItem>)}</TextField></Grid>
      <Grid item
        lg={3}
        sm={6}
        xs={12}><TextField select
          label={t('category')}
          value={filterCategory}
          onChange={(event) => { setFilterCategory(event.target.value); setPage(0); }}
          fullWidth
          size="small"><MenuItem value="">{t('filters.all')}</MenuItem>{categoryOptions.map((category) => <MenuItem key={category}
            value={category}>{category}</MenuItem>)}</TextField></Grid>
    </Grid>
  );

  return (
    <>
      {expandError && <Typography color="error"
        sx={{ mb: 1 }}>{expandError}</Typography>}
      <ListPageTemplate
        title={t('title')}
        subtitle={t('subtitle')}
        actions={<>
          {hasBulkManagePermission && <Button variant="secondary"
            onClick={() => setBulkManageOpen(true)}>{t('bulkManage.title')}</Button>}
          {hasExportPermission && <Button variant="secondary"
            onClick={handleExport}>{t('export.export')}</Button>}
          {hasWritePermission && <Button variant="primary"
            startIcon={<Add />}
            onClick={() => navigate('/range/recipes/new')}>{t('newRecipe')}</Button>}
        </>}
        quickFilterContent={quickFilters}
        advancedFilterContent={advancedFilters}
        advancedFilterActions={<Button size="md"
          variant="secondary"
          onClick={handleClearFilters}>Clear Filters</Button>}
        table={table}
      />
      {hasBulkManagePermission && selectedFranchiseId && (
        <BulkManageDialog
          open={bulkManageOpen}
          franchiseId={selectedFranchiseId!}
          onClose={() => setBulkManageOpen(false)}
          onSuccess={() => loadRecipes(debouncedSearch, statusFilter, filterCategory, page, rowsPerPage)}
        />
      )}
    </>
  );

  return (
    <PageArea>
      <Stack width="100%"
        spacing={2}>
        {/* Page header */}
        <Stack direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={{ xs: 1.5, sm: 0 }}>
          <PageTitle subTitle={t('subtitle')}>{t('title')}</PageTitle>
          <Stack direction="row"
            spacing={1}
            flexWrap="wrap"
            sx={{ '& > button': { flexGrow: { xs: 1, sm: 0 } } }}>
            {hasBulkManagePermission && (
              <Button
                variant="secondary"
                onClick={() => setBulkManageOpen(true)}
              >
                {t('bulkManage.title')}
              </Button>
            )}
            {hasExportPermission && (
              <Button
                variant="secondary"
                onClick={handleExport}
              >
                {t('export.export')}
              </Button>
            )}
            {hasWritePermission && (
              <Button
                variant="primary"
                startIcon={<Add />}
                onClick={() => navigate('/range/recipes/new')}
              >
                {t('newRecipe')}
              </Button>
            )}
          </Stack>
        </Stack>

        {/* Search + quick status filters */}
        <Grid container
          spacing={1}
          alignItems="center">
          <Grid item
            xs={12}
            sm="auto"
            sx={{ display: 'flex', alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder={t('searchPlaceholder')}
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
                endAdornment: search ? (
                  <InputAdornment position="end">
                    <Close
                      sx={{ cursor: 'pointer', fontSize: 18 }}
                      onClick={() => handleSearchChange('')}
                    />
                  </InputAdornment>
                ) : undefined,
              }}
              sx={{ width: '100%', minWidth: 240, mb: 0 }}
            />
          </Grid>
          {statusOptions.map((status) => (
            <Grid item
              key={status}>
              <Chip
                label={t(`status.${status.charAt(0).toLowerCase() + status.slice(1)}`)}
                color={statusFilter === status ? 'primary' : 'default'}
                onClick={() => {
                  setStatusFilter((prev) => (prev === status ? '' : status));
                  setPage(0);
                }}
              />
            </Grid>
          ))}
        </Grid>

        {/* Advanced filters */}
        <Accordion
          variant="outlined"
          expanded={isAdvancedOpen}
          onChange={(_, expanded) => setIsAdvancedOpen(expanded)}
          sx={{ bgcolor: 'transparent', boxShadow: 'none', border: 'none' }}
        >
          <AccordionSummary
            sx={{ px: 0 }}
          >
            <Stack direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ width: '100%' }}>
              <Stack direction="row"
                alignItems="center"
                sx={{ gap: 0.5 }}>
                <Typography variant="textSM"
                  fontWeight={600}>
                  Advanced Filters
                </Typography>
                {isAdvancedOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </Stack>
              <Button
                size="md"
                variant="secondary"
                onClick={(e) => { e.stopPropagation(); handleClearFilters(); }}
              >
                Clear Filters
              </Button>
            </Stack>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 0 }}>
            <Grid container
              spacing={3}>
              <Grid item
                lg={3}
                sm={6}
                xs={12}>
                <TextField
                  select
                  label={t('statusLabel')}
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value as RecipeStatus | '');
                    setPage(0);
                  }}
                  fullWidth
                  size="small"
                >
                  <MenuItem value="">{t('filters.all')}</MenuItem>
                  {statusOptions.map((status) => (
                    <MenuItem key={status}
                      value={status}>
                      {t(`status.${status.charAt(0).toLowerCase() + status.slice(1)}`)}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item
                lg={3}
                sm={6}
                xs={12}>
                <TextField
                  select
                  label={t('category')}
                  value={filterCategory}
                  onChange={(e) => {
                    setFilterCategory(e.target.value);
                    setPage(0);
                  }}
                  fullWidth
                  size="small"
                >
                  <MenuItem value="">{t('filters.all')}</MenuItem>
                  {categoryOptions.map((category) => (
                    <MenuItem key={category}
                      value={category}>{category}</MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Table in Section container */}
        <Section style={{ overflow: 'hidden' }}>
          <TableHeader>
            {t('title')}
            {totalCount > 0 && <Pill text={`${totalCount} ${t('title')}`} />}
          </TableHeader>

          {isLoading ? (
            <Stack alignItems="center"
              py={6}>
              <CircularProgress size={24} />
            </Stack>
          ) : error ? (
            <Stack alignItems="center"
              py={6}>
              <Typography color="error">{error}</Typography>
            </Stack>
          ) : recipes.length === 0 ? (
            <Stack alignItems="center"
              py={6}>
              <Typography color="text.secondary">{t('noRecipes')}</Typography>
            </Stack>
          ) : (
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small"
                sx={{ minWidth: 980 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 40 }} />
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{t('sku')}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{t('name')}</TableCell>
                    <TableCell align="center"
                      sx={{ whiteSpace: 'nowrap' }}>{t('statusLabel')}</TableCell>
                    <TableCell align="center"
                      sx={{ whiteSpace: 'nowrap' }}>{t('version')}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{t('category')}</TableCell>
                    <TableCell align="right"
                      sx={{ whiteSpace: 'nowrap' }}>{t('cost')}</TableCell>
                    <TableCell align="right"
                      sx={{ whiteSpace: 'nowrap' }}>{t('costPerPortion')}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{t('allergensLabel')}</TableCell>
                    <TableCell align="center"
                      sx={{ whiteSpace: 'nowrap' }}>{t('actions.title')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recipes.map((recipe) => {
                    const hasComponents = (recipe.componentCount ?? recipe.components?.length ?? 0) > 0;
                    const isExpanded = expandedIds.has(recipe.id);
                    return (
                      <Fragment key={recipe.id}>
                        <TableRow
                          hover
                          sx={{ cursor: 'pointer' }}
                          onClick={() => handleRowClick(recipe)}
                        >
                          <TableCell sx={{ px: 0.5 }}>
                            {hasComponents && (
                              <IconButton
                                size="small"
                                disabled={loadingComponentIds.has(recipe.id)}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  void toggleExpand(recipe);
                                }}
                              >
                                {loadingComponentIds.has(recipe.id) ? <CircularProgress size={16} /> : isExpanded ? <KeyboardArrowDown fontSize="small" /> : <KeyboardArrowRight fontSize="small" />}
                              </IconButton>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="textSM"
                              fontFamily="monospace">
                              {recipe.itemNumber}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="textSM"
                              fontWeight={500}>
                              {recipe.description || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={
                                recipe.status
                                  ? t(`status.${recipe.status.charAt(0).toLowerCase() + recipe.status.slice(1)}`)
                                  : 'Unknown'
                              }
                              color={recipe.status ? statusColor[recipe.status] ?? 'default' : 'default'}
                              size="small"
                              sx={{ px: '1px' }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            {`v${recipe.currentVersion ?? 1}`}
                          </TableCell>
                          <TableCell>{recipe.primaryCategory || '-'}</TableCell>
                          <TableCell align="right">
                            {recipe.costIsComplete && recipe.totalCost != null ? applyRoundAndFormat(recipe.totalCost, currencyCode) : '—'}
                          </TableCell>
                          <TableCell align="right">
                            {recipe.costIsComplete && recipe.costPerPortion != null ? applyRoundAndFormat(recipe.costPerPortion, currencyCode, 4) : '—'}
                          </TableCell>
                          <TableCell>{recipe.allergenSummary?.length ? <AllergenChips allergens={recipe.allergenSummary}
                            size="small"
                            max={3}
                            spacing={0.5} /> : '—'}</TableCell>
                          <TableCell align="center">
                            {hasWritePermission && (
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRowClick(recipe);
                                }}
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                            )}
                          </TableCell>
                        </TableRow>
                        {hasComponents && isExpanded && (
                          <TableRow>
                            <TableCell colSpan={10}
                              sx={{ py: 2, pl: { xs: 2, sm: 7 }, pr: { xs: 2, sm: 5 }, bgcolor: 'grey.50' }}>
                              <RecipeComponentsTable
                                recipeName={recipe.description}
                                components={recipe.components ?? []}
                                keyPrefix={String(recipe.id)}
                                expandedPaths={expandedComponentPaths}
                                toggleExpanded={toggleComponentPath}
                                t={t}
                                currencyCode={currencyCode}
                              />
                            </TableCell>
                          </TableRow>
                        )}
                      </Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <CustomPagination
            page={page}
            setPage={setPage}
            maxPages={maxPages}
            breakpointForChangeDisplay={120000}
            useNoDataMessage={false}
          />
        </Section>
      </Stack>
      {hasBulkManagePermission && selectedFranchiseId && (
        <BulkManageDialog
          open={bulkManageOpen}
          franchiseId={selectedFranchiseId!}
          onClose={() => setBulkManageOpen(false)}
          onSuccess={() => loadRecipes(debouncedSearch, statusFilter, filterCategory, page, rowsPerPage)}
        />
      )}
    </PageArea>
  );
};

export default RecipeV2Page;

